import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { Channel, PresenceChannel } from 'pusher-js';
import {
  BehaviorSubject,
  catchError,
  map,
  Observable,
  ReplaySubject,
  Subject,
} from 'rxjs';
import {
  CoachlogSearchPayloadDTO,
  CoachlogSearchPayloadItemsDTO,
} from 'src/app/common/dtos/coachlog-search-payload.dto';
import { LogTypeDTO } from 'src/app/common/dtos/log-type.dto';
import { BooleanNumber } from 'src/app/common/utilities/enums/boolean-number.enum';
import {
  AddCompetencyPayload,
  CreateOptionPayload,
  CreateWillPayload,
  FeedbackPayload,
  GetCoachLogTypesPayload,
  UpdateAttendeePayload,
  UpdateLogPayload,
  UpdateShadowerPayload,
  willPayloadToAPIPayload,
} from 'src/app/private/shared/types/payloads/coaching-log.payloads';

import { APICoreService } from '../../../../common/services/api-core/api-core.service';
import { AvatarDTO } from '../../components/avatar/avatar.dto';
import { OptionDTO, WillDTO } from '../../dtos/attendee-rubric.dto';
import {
  CoachingSessionAttendeeDTO,
  CoachingSessionDTO,
  CoachingSessionShadowerDTO,
  NewCoachingSessionItemsPayloadDTO,
} from '../../dtos/coaching-session.dto';
import { CreatedCoachingLogDTO } from '../../dtos/created-coaching-log.dto';
import { ResourceListItemDTO } from '../../dtos/resources.dto';
import {
  assessmentsDTOFromAPIResponse,
  attendeeRubricsDTOFromAPIResponse,
  createOptionPayloadToCreateOptionAPIPayload,
  goalDTOFromAPIResponse,
} from '../../helpers/translators/attendee-rubrics-dto.translator';
import {
  badgeUserListDTOFromAPIResponse,
  coachingSessionDTOFromAPIResponse,
  coachlogSearchPayloadItemsFromComponent,
  feedbackListDTOFromAPIResponse,
  individualCoachingSessionDTOFromAPIResponse,
  nextSessionDTOFromAPIResponse,
  upcomingCoachingSessionsDTOFromAPIResponse,
} from '../../helpers/translators/coaching-session-dto.translator';
import {
  createdCoachingLogDTOFromAPIResponse,
  newCoachingSessionPayloadDtoFromComponent,
} from '../../helpers/translators/created-coaching-log-dto.translator';
import {
  UpdateGoal,
  UpdateNotes,
} from '../../state/coaching-log/coaching-log.actions';
import { SetGoalPayload } from '../../types/payloads/set-goal.payload';
import {
  AssessmentAPIResponse,
  AttendeeRubricsAPIResponse,
  BadgeUserAPIResponse,
  CreatedCoachingLogAPIResponse,
  FeedbackAPIResponse,
  GoalAPIResponse,
  IndividualCoachingSessionDataAPIResponse,
  UpcomingCoachingSessionsDataAPIResponse,
} from '../../types/responses/coaching-log.responses';
import { UserPusherService } from '../pusher/user.pusher.service';
import { CoachingLogPusherService } from './coaching-log-pusher.service';
import { IndicatorAssessmentPayload } from './coaching-log-service.dto';
import { convertFromSetGoalPayload } from './coaching-log-service.translators';

@Injectable({ providedIn: 'root' })
export class CoachingLogService {
  private searchFiltersData: CoachlogSearchPayloadDTO = {
    keywords: '',
    from_date: 0,
    to_date: 0,
    district_id: 0,
    competencies: [],
    schools: [],
    coach_user_id: [],
    attendees: [],
    logtype: 0,
    per_page: 10,
    sort_order: 'DESC',
    ownonly: 0,
  };

  private searchFiltersSource = new BehaviorSubject(this.searchFiltersData);

  public searchFiltersObs = this.searchFiltersSource.asObservable();

  private searchFiltersItemsData: CoachlogSearchPayloadItemsDTO = {
    keywords: '',
    from_date: 0,
    to_date: 0,
    competency_items: [],
    school_items: [],
    coach_items: [],
    coachee_items: [],
    per_page: 10,
    sort_order: 'DESC',
    ownonly: 0,
  };

  blankSearchOptions: CoachlogSearchPayloadDTO = {
    keywords: '',
    from_date: 0,
    to_date: 0,
    district_id: 0,
    competencies: [],
    schools: [],
    coach_user_id: [],
    attendees: [],
    logtype: 0,
    per_page: 10,
    ownonly: 0,
    sort_order: 'DESC',
  };

  blankSearchOptionsItems: CoachlogSearchPayloadItemsDTO = {
    keywords: '',
    from_date: 0,
    to_date: 0,
    competency_items: [],
    school_items: [],
    coach_items: [],
    coachee_items: [],
    per_page: 10,
    ownonly: 0,
    sort_order: 'DESC',
  };

  private searchFiltersItemsSource = new BehaviorSubject(
    this.searchFiltersItemsData
  );

  public searchFiltersItemsObs = this.searchFiltersItemsSource.asObservable();

  public channel: PresenceChannel;

  public pusherAttendees: AvatarDTO[];

  public coachlogSearchOptionItems: CoachlogSearchPayloadItemsDTO = {
    keywords: '',
    from_date: 0,
    to_date: 0,
    competency_items: [],
    school_items: [],
    coach_items: [],
    coachee_items: [],
    per_page: 10,
    sort_order: 'DESC',
    ownonly: 0,
  };

  public coachlogCreated: ReplaySubject<CoachingSessionDTO> =
    new ReplaySubject();

  public coachlogError: Subject<string> = new Subject();

  public coachlogDeleted: ReplaySubject<CoachingSessionDTO> =
    new ReplaySubject();

  constructor(
    private apiService: APICoreService,
    private router: Router,
    private store: Store,
    public logPusherService: CoachingLogPusherService,
    private userPusherService: UserPusherService
  ) {}

  createLog(
    coach_id: number,
    coachee_ids: number[],
    shouldRedirect: boolean
  ): Observable<CreatedCoachingLogDTO | null> {
    return this.apiService
      .postRequest('growelab/coaching/quick-start', {
        coach_id,
        coachee_ids,
      })
      .pipe(
        map((res) => {
          const responseDTO = createdCoachingLogDTOFromAPIResponse(
            res as CreatedCoachingLogAPIResponse
          );
          if (res.item.id && shouldRedirect) {
            this.router.navigate([`/coaching/log/${res.item.id}/info`]);
          }
          return responseDTO;
        }),
        catchError((err) => {
          throw err;
        })
      );
  }

  getLogTypes(districtId: number): Observable<LogTypeDTO[] | null> {
    return this.apiService
      .getRequest(
        `egrowe/coachlogs/types?per_page=100&district_id=${districtId}`
      )
      .pipe(map((response) => response.items));
  }

  getPreviousLogs(
    attendeeId: number,
    startDate: number,
    logType: number,
    perPage = 40
  ): Observable<CoachingSessionDTO[] | null> {
    return this.apiService
      .getRequest(
        `egrowe/coachlogs?to_date=${startDate}&page=1&per_page=${perPage}&logtype=${logType}&attendees[0]=${attendeeId}`
      )
      .pipe(map((response) => response.items));
  }

  disconnectFromPusherChannel(id: number) {
    this.logPusherService.removeChannel(`coachlog-v2-${id}`);
  }

  connectToPusherChannel(id: number) {
    this.logPusherService.addChannel(`coachlog-v2-${id}`);
  }

  getChannel(id: number): Channel {
    return this.logPusherService.getChannel(`coachlog-v2-${id}`);
  }

  updateNotes(logId: number, content: string) {
    return (
      this.apiService
        .putRequest(`egrowe/coachlog-v2/${logId}/notes`, {
          content,
        })
        .subscribe(() =>
          this.store.dispatch(
            new UpdateNotes({ notes: content, coachlogId: logId })
          )
        ),
      catchError((err) => {
        throw err;
      })
    );
  }

  getCoachingSession(id: number): Observable<CoachingSessionDTO> {
    this.connectToPusherChannel(id);

    return this.apiService.getRequest(`egrowe/coachlogs-v2/${id}`).pipe(
      map((res) => {
        const responseDTO = individualCoachingSessionDTOFromAPIResponse(
          res as IndividualCoachingSessionDataAPIResponse
        );
        return responseDTO;
      })
    );
  }

  getAttendeeRubrics(id: number) {
    return this.apiService
      .getRequest(`growelab/coaching/${id}/attendee-rubrics`)
      .pipe(
        map((res) => {
          const responseDTO = attendeeRubricsDTOFromAPIResponse(
            res as AttendeeRubricsAPIResponse
          );
          Object.keys(responseDTO.decomposedByUser).forEach((userId) => {
            this.userPusherService.addChannel(`growth-${userId}`);
          });
          return responseDTO;
        })
      );
  }

  assessIndicator(payload: IndicatorAssessmentPayload) {
    const postData: IndicatorAssessmentPayload = {
      egrowe_coachlog_id: 0,
      from_egrowe_portfolio: BooleanNumber.FALSE,
      ...payload,
    };

    return this.apiService
      .postRequest('egrowe/indicator-assessments', postData)
      .pipe(
        map((res) => {
          const responseDTO = assessmentsDTOFromAPIResponse(
            res.item as AssessmentAPIResponse
          );
          return responseDTO;
        }),
        catchError((err) => {
          throw err;
        })
      );
  }

  deleteAssessment(assessmentId: number, logId: number) {
    return this.apiService
      .deleteRequest(`egrowe/indicator-assessments/${assessmentId}/${logId}`)
      .pipe(
        map((res) => res),
        catchError((err) => {
          throw err;
        })
      );
  }

  getUpcomingSessions() {
    let currentDate: Date | number = new Date();
    currentDate.setHours(0, 0, 0, 0);
    currentDate = currentDate.getTime() / 1000;

    const twelveHours = 3600 * 12;
    const sevenDays = 86400 * 7;

    const requestOptions = {
      keywords: '',
      to_date: new Date().getTime() / 1000 + twelveHours + sevenDays,
      competency_items: [],
      school_items: [],
      coach_items: [],
      coachee_items: [],
      per_page: 50,
      sort_order: 'ASC',
      ownonly: 1,
      from_date: currentDate,
    };
    this.setFilters(requestOptions);
    return this.getSessions();
  }

  getSessions() {
    const filterFreezeValues: CoachlogSearchPayloadDTO =
      coachlogSearchPayloadItemsFromComponent(
        this.searchFiltersItemsSource.value
      );

    return this.apiService
      .getRequest(`egrowe/coachlogs`, filterFreezeValues)
      .pipe(
        map((res) => {
          const responseDTO = upcomingCoachingSessionsDTOFromAPIResponse(
            res as UpcomingCoachingSessionsDataAPIResponse
          );
          return responseDTO;
        })
      );
  }

  setFilters(filters: CoachlogSearchPayloadItemsDTO) {
    this.searchFiltersItemsSource.next(filters);
  }

  resetFilters() {
    this.searchFiltersItemsSource.next(this.blankSearchOptionsItems);
  }

  setGoal(payload: SetGoalPayload) {
    return (
      this.apiService
        .postRequest('egrowe/goals', convertFromSetGoalPayload(payload))
        .subscribe((res) => {
          const goalDTO = goalDTOFromAPIResponse(res.item as GoalAPIResponse);
          this.store.dispatch(new UpdateGoal(goalDTO));
        }),
      catchError((err) => {
        throw err;
      })
    );
  }

  createCoachinglog(payloadItems: NewCoachingSessionItemsPayloadDTO) {
    let newPayload = newCoachingSessionPayloadDtoFromComponent(payloadItems);
    newPayload = JSON.parse(JSON.stringify(newPayload));

    return (
      this.apiService.postRequest('egrowe/coachlogs', newPayload).subscribe(
        (res) => {
          if (res) {
            this.coachlogCreated.next(
              coachingSessionDTOFromAPIResponse(res.item)
            );
            this.coachlogError.next('');
          }
        },
        (err) => {
          this.coachlogError.next(err.message);
        }
      ),
      catchError((err) => {
        throw err;
      })
    );
  }

  createOption(coachlogId: number, options: CreateOptionPayload) {
    return (
      this.apiService
        .postRequest(
          `egrowe/coachlogs/${coachlogId}/options`,
          createOptionPayloadToCreateOptionAPIPayload(options)
        )
        .subscribe(),
      catchError((err) => {
        throw err;
      })
    );
  }

  updateOption(optionDTO: OptionDTO) {
    return (
      this.apiService
        .putRequest(`egrowe/coachlogs/options/${optionDTO.id}`, optionDTO)
        .subscribe(),
      catchError((err) => {
        // This error would be important, as the frontend will not reflect that the data wasn't saved
        throw err;
      })
    );
  }

  deleteOption(optionId: number) {
    return (
      this.apiService
        .deleteRequest(`egrowe/coachlogs/options/${optionId}`)
        .subscribe(),
      catchError((err) => {
        throw err;
      })
    );
  }

  addResourceToOption(resource: ResourceListItemDTO, optiondId: number) {
    return (
      this.apiService
        .postRequest(`egrowe/coachlogs/options/${optiondId}/resources`, {
          elearn_resource_id: resource.id,
        })
        .subscribe(),
      catchError((err) => {
        throw err;
      })
    );
  }

  removeResourceFromOption(resourceId: number, optiondId: number) {
    return (
      this.apiService
        .deleteRequest(
          `egrowe/coachlogs/options/${optiondId}/resources/${resourceId}`
        )
        .subscribe(),
      catchError((err) => {
        throw err;
      })
    );
  }

  createWill(payload: CreateWillPayload) {
    return (
      this.apiService
        .postRequest('egrowe/coachlogs/wills', willPayloadToAPIPayload(payload))
        .subscribe(),
      catchError((err) => {
        throw err;
      })
    );
  }

  deleteWill(willId: number) {
    return (
      this.apiService
        .deleteRequest(`egrowe/coachlogs/wills/${willId}`)
        .subscribe(),
      catchError((err) => {
        throw err;
      })
    );
  }

  updateWill(payload: WillDTO) {
    return (
      this.apiService
        .putRequest(
          `egrowe/coachlogs/wills/${payload.id}`,
          willPayloadToAPIPayload(payload)
        )
        .subscribe(),
      catchError((err) => {
        throw err;
      })
    );
  }

  getCompetencies(logId: number) {
    return this.apiService.getRequest(
      `growelab/coaching/${logId}/competencies`
    );
  }

  addCompetency(payload: AddCompetencyPayload, logId: number) {
    return this.apiService.postRequest(
      `egrowe/coachlogs/${logId}/standards`,
      payload
    );
  }

  deleteCompetency(standardId: number, logId: number) {
    return this.apiService.deleteRequest(
      `egrowe/coachlogs/${logId}/standards?egrowe_standard_id=${standardId}`
    );
  }

  deleteCoachlog(session: CoachingSessionDTO) {
    return this.apiService
      .deleteRequest(`egrowe/coachlogs/${session.id}`)
      .subscribe((res) => {
        if (res) {
          this.coachlogDeleted.next(session);
        }
      });
  }

  updateCoachingLog(coachingLogId: number, payload: UpdateLogPayload) {
    return (
      this.apiService
        .putRequest(`egrowe/coachlogs-v2/${coachingLogId}`, payload)
        .subscribe(),
      catchError((err) => {
        throw err;
      })
    );
  }

  getCoachlogTypes(districtId?: number) {
    const payload: GetCoachLogTypesPayload = { per_page: 100 };
    if (districtId) {
      payload.district_id = districtId;
    }
    return this.apiService.getRequest('egrowe/coachlogs/types', payload).pipe(
      map((res) => res),
      catchError((err) => {
        throw err;
      })
    );
  }

  addAttendee(userId: number, coachlogId?: number) {
    return (
      this.apiService
        .postRequest(`egrowe/coachlogs/${coachlogId}/attendees`, {
          user_id: userId,
        })
        .subscribe(),
      catchError((err) => {
        throw err;
      })
    );
  }

  deleteAttendee(attendee: CoachingSessionAttendeeDTO) {
    return this.apiService.deleteRequest(
      `egrowe/coachlogs/attendees/${attendee.id}`
    );
  }

  updateAttendee(attendeeId: number, changes: UpdateAttendeePayload) {
    return this.apiService.putRequest(
      `egrowe/coachlogs/attendees/${attendeeId}`,
      changes
    );
  }

  getAbsentReasons() {
    return this.apiService
      .getRequest('egrowe/coachlogs/absent-reasons?per-page=100')
      .pipe(
        map((res) => res),
        catchError((err) => {
          throw err;
        })
      );
  }

  addShadower(userId: number, coachlogId?: number) {
    return (
      this.apiService
        .postRequest(`egrowe/coachlogs/${coachlogId}/shadowers`, {
          user_id: userId,
        })
        .subscribe(),
      catchError((err) => {
        throw err;
      })
    );
  }

  deleteShadower(shadower: CoachingSessionShadowerDTO) {
    return this.apiService.deleteRequest(
      `egrowe/coachlogs/shadowers/${shadower.id}`
    );
  }

  updateShadower(
    attendeeId: number,
    logId: number,
    changes: UpdateShadowerPayload
  ) {
    return this.apiService.putRequest(
      `egrowe/coachlogs/${logId}/shadowers/${attendeeId}`,
      changes
    );
  }

  getFeedback(logId: number) {
    return this.apiService
      .getRequest(`egrowe/coachlog/feedback/by-coachlog/${logId}`)
      .pipe(
        map((res) =>
          feedbackListDTOFromAPIResponse(
            res as { items: FeedbackAPIResponse[] }
          )
        )
      );
  }

  getBadgeUsers(logId: number) {
    return this.apiService
      .getRequest(`growelab/coachlogs/${logId}/badges-earned`)
      .pipe(
        map((res) =>
          badgeUserListDTOFromAPIResponse(
            res as { items: BadgeUserAPIResponse[] }
          )
        )
      );
  }

  getAttendeesNeedNextLog(logId: number) {
    return this.apiService
      .getRequest(`egrowe/coachlogs/${logId}/attendees-need-next-log`)
      .pipe(
        map((res) => {
          if (res.attendeesScheduled[0]) {
            res.attendeesScheduled[0].nextLog = nextSessionDTOFromAPIResponse(
              res.attendeesScheduled[0].nextLog
            );
          }
          return res;
        })
      );
  }

  submitFeedback(payload: FeedbackPayload) {
    return this.apiService.postRequest('egrowe/coachlog/feedback', payload);
  }

  editFeedback(itemId: number, changedContent: string) {
    return this.apiService.putRequest(`egrowe/coachlog/feedback/${itemId}`, {
      content: changedContent,
    });
  }

  deleteFeedback(itemId: number) {
    return this.apiService.deleteRequest(`egrowe/coachlog/feedback/${itemId}`);
  }
}
